// ForgotPassword — step 1 of the reset flow: enter the phone number that
// should receive the one-time code. Mocked (no API): validate, fake a
// request, then move to OTP entry carrying the phone via router state.

import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import * as yup from 'yup';
import { Button, Field, PhoneInput } from '@/components/common';
import { countries, defaultCountry } from '@/mocks/countries';
import AuthLayout from '@/layout/AuthLayout';
import { OTPType, useSendOtpMutation } from '@/api/endpoints/authApi';
import type { ApiErrorShape } from '@/api/types';
import useOtpCooldown from '@/hooks/useOtpCooldown';
import { phoneOtpKey } from '@/redux/otp/otpKeys';
import AuthBack from './AuthBack';
import { validateAll, validateField, type FormErrors } from './validation';

const schema = yup.object({
  phone: yup
    .string()
    .required('error Phone number is required')
    .matches(/^\d{7,12}$/, { message: 'error_invalid_phone_number', excludeEmptyString: true }),
});

const ForgotPassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [sendOtp, { isLoading: submitting }] = useSendOtpMutation();

  const [dialCode, setDialCode] = useState(defaultCountry.dial);
  const [values, setValues] = useState({ phone: '' });
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitted, setSubmitted] = useState(false);

  // Keyed off the LIVE typed number, so the countdown engages only when it
  // matches the number a code was just sent to — a fresh number never blocks.
  const cooldown = useOtpCooldown(
    values.phone ? phoneOtpKey(OTPType.RESET_PASSWORD, dialCode, values.phone) : null,
  );

  const setPhone = (phone: string) => {
    const next = { phone };
    setValues(next);
    if (submitted) {
      validateField(schema, 'phone', next).then((message) =>
        setErrors((prev) => ({ ...prev, phone: message })),
      );
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    const nextErrors = await validateAll(schema, values);
    setErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) return;
    if (cooldown.active) return;

    try {
      await sendOtp({
        countryCode: dialCode,
        mobileNumber: values.phone,
        otpType: OTPType.RESET_PASSWORD,
      }).unwrap();
      toast.success(t('toast_otp_sent'));
      navigate('/verify-otp', {
        state: { otpType: OTPType.RESET_PASSWORD, dialCode, phone: values.phone },
      });
    } catch (err) {
      toast.error((err as ApiErrorShape)?.msg ?? t('error_generic'));
    }
  };

  return (
    <AuthLayout variant='login'>
      <AuthBack onClick={() => navigate('/login')} />
      <div className='auth-page'>
        <header className='auth-page__header'>
          <h1 className='auth-page__title'>{t('text_otp_verification')}</h1>
          <p className='auth-page__subtitle'>{t('text_otp_send_subtitle')}</p>
        </header>

        <form className='auth-page__form' onSubmit={handleSubmit} noValidate>
          <Field
            label={t('field_phone_number')}
            htmlFor='fp-phone'
            error={errors.phone ? t(errors.phone) : undefined}
          >
            <PhoneInput
              id='fp-phone'
              countries={countries}
              dialCode={dialCode}
              onDialCodeChange={setDialCode}
              value={values.phone}
              onChange={setPhone}
              error={!!errors.phone}
              placeholder={t('field_phone_placeholder')}
            />
          </Field>

          <Button
            type='submit'
            size='lg'
            loading={submitting}
            disabled={cooldown.active}
            className='auth-page__submit'
          >
            {cooldown.active
              ? t('button_get_otp_in', { time: cooldown.label })
              : t('button_get_otp')}
          </Button>

          <p className='auth-page__alt'>
            <button type='button' className='auth-page__link' onClick={() => navigate('/login')}>
              {t('text_go_to_login')}
            </button>
          </p>
        </form>
      </div>
    </AuthLayout>
  );
};

export default ForgotPassword;
